import { App, Checkbox, Divider, Form, Input, Modal, Select, Space, Spin } from "antd";
import { CloseSquareFilled, SaveFilled } from '@ant-design/icons';
import { useEffect, useState } from "react";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";

export const CustomerUserEditor = ({ isOpen, user, customer, onUserSaved, onUserCancelled }) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();

    const [organizationModules, setOrganizationModules] = useState([]);
    const [selectedModules, setSelectedModules] = useState([]);
    const [sendInvite, setSendInvite] = useState(true);
    const [loadingModules, setLoadingModules] = useState(false);

    const isNewUser = !user?.UserID;

    const handleUserEditFinished = async (values) => {
        console.log("handleUserEditFinished", values);
        console.log("user prop:", user);

        const userData = { ...user, ...values };
        console.log("userData merged:", userData);
        console.log("userData.UserID:", userData.UserID);

        if (userData.UserID) {
            // Update existing user
            const activeModules = selectedModules.reduce((acc, shortName) => {
                acc[shortName] = true;
                return acc;
            }, {});

            const updatePayload = {
                UserID: userData.UserID,
                FirstName: values.FirstName,
                LastName: values.LastName,
                Email: values.Email,
                IsAdmin: values.IsAdmin ? 1 : 0,
                IsActive: 1,
                modifiedFields: ['FirstName', 'LastName', 'IsAdmin', 'Modules', 'Role'],
                activeModules: activeModules,
                activeRoles: {}
            };

            try {
                const response = await execute(
                    'PUT',
                    `${protectedResources.api.baseEndPoint}/customerUsers`,
                    updatePayload
                );
                if (response.success) {
                    message.success("User updated successfully");
                    onUserSaved();
                } else {
                    message.error(response.message || 'Failed to update user');
                }
            } catch (error) {
                message.error('Error updating user: ' + error.message);
            }
        } else {
            // Create new user
            const modulesString = selectedModules.length > 0
                ? selectedModules.join(',')
                : null;

            const createPayload = {
                organizationName: values.FirstName,
                address: values.LastName,
                domain: values.IsAdmin ? 1 : 0,
                adminEmail: values.Email,
                selectedCustomer: {
                    Customer_ID: customer.Customer_ID,
                    CustomerName: customer.CustomerName
                },
                modulesAssigned: selectedModules.reduce((acc, shortName) => {
                    acc[shortName] = true;
                    return acc;
                }, {}),
                selectedRoles: {},
                sendInvite: sendInvite
            };

            console.log('Creating customer user with payload:', createPayload);

            try {
                const response = await execute(
                    'POST',
                    `${protectedResources.api.baseEndPoint}/customerUsers`,
                    createPayload
                );
                if (response.success) {
                    const successMessage = sendInvite
                        ? "User created successfully. Invitation sent!"
                        : "User created successfully. Invitation not sent.";
                    message.success(successMessage);
                    onUserSaved();
                } else {
                    message.error(response.message || 'Failed to create user');
                }
            } catch (error) {
                message.error('Error creating user: ' + error.message);
            }
        }
    };

    const handleUserEditCancelled = () => {
        console.log("handleUserEditCancelled");
        onUserCancelled();
    };

    // Fetch organization modules when creating/editing a user
    useEffect(() => {
        const fetchOrganizationModules = async () => {
            if (customer && customer.Org_ID && isOpen) {
                try {
                    setLoadingModules(true);
                    console.log('Fetching modules for organization:', customer.Org_ID);

                    const response = await execute(
                        'GET',
                        `${protectedResources.api.baseEndPoint}/organizations/${customer.Org_ID}/modules`
                    );

                    if (response.success && response.data) {
                        console.log('Organization modules fetched:', response.data);
                        setOrganizationModules(response.data);

                        // For new users, select all modules by default
                        // For existing users, parse their current modules
                        if (isNewUser) {
                            const allModuleShortNames = response.data.map(m => m.ShortName);
                            setSelectedModules(allModuleShortNames);
                        } else if (user.Modules) {
                            const userModules = user.Modules.split(',').map(m => m.trim());
                            setSelectedModules(userModules);
                        }
                    } else {
                        message.warning('No modules found for this organization');
                        setOrganizationModules([]);
                        setSelectedModules([]);
                    }
                } catch (error) {
                    console.error('Failed to fetch organization modules:', error);
                    message.error('Failed to load organization modules');
                    setOrganizationModules([]);
                    setSelectedModules([]);
                } finally {
                    setLoadingModules(false);
                }
            }
        };

        fetchOrganizationModules();
    }, [isOpen, customer, user]);

    useEffect(() => {
        if (isOpen) {
            if (isNewUser) {
                form.setFieldsValue({
                    FirstName: '',
                    LastName: '',
                    Email: '@' + customer.Domain,
                    IsAdmin: false,
                    Role: ''
                });
            } else {
                form.setFieldsValue({
                    UserID: user.UserID,
                    FirstName: user.FirstName,
                    LastName: user.LastName,
                    Email: user.Email,
                    IsAdmin: user.IsAdmin,
                    Role: user.Role || ''
                });
            }
        }
    }, [isOpen, user, form, customer, isNewUser]);

    // Email validation function
    const checkIfUserWithEmailExists = async (email) => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customerUsers/exists/${email}`
            );
            if (response.success) {
                return response.data;
            } else {
                return { exists: false };
            }
        } catch (error) {
            console.error('Error checking if user exists:', error);
            return { exists: false };
        }
    };

    return (
        <Modal
            open={isOpen}
            title={isNewUser ? "Create Customer User" : "Edit Customer User"}
            okText={<Space><SaveFilled />Save</Space>}
            cancelText={<Space><CloseSquareFilled />Cancel</Space>}
            okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
            onCancel={handleUserEditCancelled}
            destroyOnClose
            width={600}
            modalRender={(dom) => (
                <Form
                    layout="horizontal"
                    form={form}
                    name="userEditorForm"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    clearOnDestroy
                    onFinish={handleUserEditFinished}
                >
                    {dom}
                </Form>
            )}>
            <Form.Item
                name="UserID"
                label="User ID"
                hidden>
                <Input />
            </Form.Item>

            <Form.Item
                name="FirstName"
                label="First Name"
                rules={[{ required: true, message: 'First Name is required' }]}>
                <Input />
            </Form.Item>

            <Form.Item
                name="LastName"
                label="Last Name"
                rules={[{ required: true, message: 'Last Name is required' }]}>
                <Input />
            </Form.Item>

            <Form.Item
                name="Email"
                label="Email"
                hasFeedback
                rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Invalid email address' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || customer.Domain === value.split('@')[1]) {
                                return Promise.resolve();
                            }
                            return Promise.reject(`Email domain must match ${customer.Domain}`);
                        },
                    }),
                    // Validate from server if email is not already taken (only for new users)
                    () => ({
                        validator: async (_, value) => {
                            if (value && isNewUser) {
                                try {
                                    const response = await checkIfUserWithEmailExists(value);
                                    if (response.exists === false) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject('A user with this email already exists');
                                } catch (error) {
                                    console.error('Error checking if user exists:', error);
                                    return Promise.reject('Error validating email: ' + error.message);
                                }
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}>
                <Input disabled={!isNewUser} />
            </Form.Item>

            <Form.Item
                name="IsAdmin"
                label="Admin User"
                valuePropName="checked">
                <Checkbox>Grant admin privileges</Checkbox>
            </Form.Item>

            {/*
            <Form.Item
                name="Role"
                label="Role Description">
                <Input placeholder="Optional role description" />
            </Form.Item>
            */}

            <Divider orientation="left">Module Assignment</Divider>

            {loadingModules ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin tip="Loading organization modules..." />
                </div>
            ) : organizationModules.length > 0 ? (
                <>
                    <Form.Item
                        label="Assign Modules"
                        help="Select modules to assign to this user. All modules are selected by default.">
                        <Checkbox.Group
                            options={organizationModules.map(module => ({
                                label: module.ModuleName,
                                value: module.ShortName
                            }))}
                            value={selectedModules}
                            onChange={setSelectedModules}
                            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                        />
                    </Form.Item>

                    {isNewUser && (
                        <>
                            <Divider orientation="left">Invitation Options</Divider>

                            <Form.Item
                                help="If checked, an invitation email will be sent to the user immediately after creation.">
                                <Checkbox
                                    checked={sendInvite}
                                    onChange={(e) => setSendInvite(e.target.checked)}
                                >
                                    Send invitation email immediately
                                </Checkbox>
                            </Form.Item>
                        </>
                    )}
                </>
            ) : (
                <Form.Item
                    label="Modules"
                    help="No modules available for this organization.">
                    <span style={{ color: '#999' }}>No modules found</span>
                </Form.Item>
            )}
        </Modal>
    );
};
